import styled, { css } from 'styled-components';

import { COLORS } from '@theme/colors';
import { media } from '@theme/media';
import { FONT_SIZE, LETTER_SPACING, typography } from '@theme/font';
import { NARROW_CONTAINER_WIDTH } from '@components/blocks/blocks.constants';
import { OptimisedImageWrapper } from '@components/optimisedImageWrapper';
import { OFFSET } from '@theme/helpers';

export const StyledImageWrapper = styled(OptimisedImageWrapper)`
  display: flex;
  width: 160px;
  height: 160px;
  align-items: end;
  justify-content: center;

  ${media.tablet} {
    width: 180px;
    height: 180px;
  }

  ${media.desktop} {
    width: 200px;
    height: 200px;
    align-items: center;
  }

  ${media.bigDesktop} {
    width: 288px;
    height: 288px;
  }
`;

const sideMargin = 18;
const gap = 36;

const narrowListStyles = css`
  max-width: ${NARROW_CONTAINER_WIDTH}px;

  > button {
    margin: 0 auto;

    div {
      ${typography({ size: FONT_SIZE.L })};
      margin: 0;
    }
  }
`;

const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${OFFSET.S};
  justify-items: center;
  align-items: start;

  ${media.tablet} {
    grid-template-columns: repeat(4, minmax(150px, 1fr));
    gap: ${OFFSET.M};
  }

  ${media.bigDesktop} {
    gap: ${OFFSET.L};
  }
`;

const columnAlphabetStyles = css`
  display: flex;
  column-gap: ${gap}px;
  row-gap: ${gap * 2}px;
  max-width: 576px;
  flex-wrap: wrap;
  margin: 0 auto;
  padding: 0 ${sideMargin}px;

  ${media.tablet} {
    row-gap: ${gap * 3}px;
  }
`;

type ContainerProps = {
  isFeatured: boolean;
  isList: boolean;
};

export const Container = styled.div<ContainerProps>`
  margin: 0 auto;
  padding: 0 ${sideMargin}px;

  ${({ isFeatured, isList }) => {
    if (isFeatured && isList) return narrowListStyles;
    if (!isList) return gridStyles;
    if (!isFeatured && isList) return columnAlphabetStyles;
  }}
`;

const textStyles = css`
  ${typography({ size: FONT_SIZE.XS, spacing: LETTER_SPACING.S })};
  color: ${COLORS.BLACK};
  text-align: left;
  padding: 0;
  width: 100%;

  ${media.mobile} {
    ${typography({ spacing: LETTER_SPACING.S })};
  }
`;

export const Text = styled.div`
  ${textStyles};
  text-align: center;
  margin-top: ${OFFSET.S};
`;

export const ArtistImage = styled.img`
  max-width: 160px;
  max-height: 160px;

  ${media.tablet} {
    max-width: 180px;
    max-height: 180px;
  }

  ${media.desktop} {
    max-width: 200px;
    max-height: 200px;
  }

  ${media.bigDesktop} {
    max-width: 288px;
    max-height: 288px;
  }
`;

export const LinkWrapper = styled.button`
  display: flex;
  flex-direction: column;
  margin-bottom: ${OFFSET.M};
  align-items: center;
  padding: 0;
  justify-content: end;
  width: 160px;
  position: relative;

  ${media.tablet} {
    width: 180px;
  }

  ${media.desktop} {
    width: 200px;
  }

  ${media.bigDesktop} {
    margin-bottom: 72px;
    width: 288px;
  }
`;

export const ButtonLink = styled.button<{ isCentered?: boolean; isLarge?: boolean }>`
  ${textStyles};
  text-align: ${({ isCentered }) => (isCentered ? 'center' : 'left')};
  font-size: ${({ isLarge }) => (isLarge ? '30px' : '15px')};
  line-height: ${({ isLarge }) => (isLarge ? '42px' : '24px')};
  ${media.desktop} {
    font-size: ${({ isLarge }) => (isLarge ? '40px' : '15px')};
    line-height: ${({ isLarge }) => (isLarge ? '48px' : OFFSET.S)};
  }
`;

export const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;

  > svg {
    width: 50%;
  }
`;
